import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { API_ENDPOINTS } from '../config/api';

const YearStatusContext = createContext();

export const YearStatusProvider = ({ children }) => {
  const [yearStatus, setYearStatus] = useState({
    currentOpenYear: new Date().getFullYear(),
    availableYears: [],
    closedYears: [],
    loading: true,
    error: null
  });

  const fetchYearStatus = useCallback(async () => {
    try {
      setYearStatus(prev => ({ ...prev, loading: true, error: null }));

      const response = await fetch(API_ENDPOINTS.YEAR_STATUS, {
        credentials: 'include',
      });

      if (response.ok) {
        const data = await response.json();
        setYearStatus({
          currentOpenYear: data.year_status.current_open_year,
          availableYears: data.year_status.available_years,
          closedYears: data.year_status.closed_years,
          loading: false,
          error: null
        });
      } else {
        throw new Error('Failed to fetch year status');
      }
    } catch (err) {
      setYearStatus(prev => ({
        ...prev,
        loading: false,
        error: err.message
      }));
    }
  }, []);

  useEffect(() => {
    fetchYearStatus();
  }, [fetchYearStatus]);

  // Helper functions
  const isYearClosed = (year) => {
    return yearStatus.closedYears.includes(year);
  };

  const canNavigateToYear = (year) => {
    return yearStatus.availableYears.includes(year);
  };

  const getMaxNavigableYear = () => {
    return Math.max(...yearStatus.availableYears, yearStatus.currentOpenYear + 1);
  };

  const getMinNavigableYear = () => {
    return Math.min(...yearStatus.availableYears);
  };

  const value = {
    yearStatus: {
      currentOpenYear: yearStatus.currentOpenYear,
      availableYears: yearStatus.availableYears,
      closedYears: yearStatus.closedYears
    },
    loading: yearStatus.loading,
    error: yearStatus.error,
    isYearClosed,
    canNavigateToYear,
    getMaxNavigableYear,
    getMinNavigableYear,
    refetchYearStatus: fetchYearStatus
  };

  return (
    <YearStatusContext.Provider value={value}>
      {children}
    </YearStatusContext.Provider>
  );
};

export const useYearStatus = () => {
  const context = useContext(YearStatusContext);
  if (!context) {
    throw new Error('useYearStatus must be used within a YearStatusProvider');
  }
  return context;
};

export default YearStatusContext;
